import { lightBlue } from "@mui/material/colors";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { OWN_STRIKE_ATTRIBUTE } from "work/proposals/redlining/__diff/own-strike";

/**
 * Which side a removed row's redline belongs to, which is all its colour says. The red and blue rule
 * of `EVENT_NEGOTIATION_DESIGN.md` ("Red and blue lining", #6885): red for a removal the other party
 * published that we have not answered, blue for a removal of ours. A removal nobody has to answer any
 * more — one we accepted, or a row the negotiation dropped in an earlier turn — is neither, and is
 * struck in the plain text tone.
 */
export enum RemovedRowRedlineTone {
  Incoming = "incoming",
  Own = "own",
  Settled = "settled",
}

/**
 * The attribute a removed redline row carries, naming its tone, so a test can find the row and tell
 * the three apart without reading a colour.
 */
export const REMOVED_ROW_REDLINE_ATTRIBUTE = "data-removed-redline";

/** What a fee schedule category, team member or document redline says about its own removal. */
export type RemovableRedline = {
  readonly isRemoved: boolean;
  readonly isResolved: boolean;
  readonly isNewlyRemoved: boolean;
  readonly currentEntry?: unknown;
};

/**
 * The tone of a redline row's removal, or undefined when the row is not a removal.
 *
 * A row with a current entry is not removed, whatever `isRemoved` says: a fee schedule category counts
 * a rejected removal as removed, although the category is back.
 */
export function removedRowRedlineTone(redline: RemovableRedline): RemovedRowRedlineTone | undefined {
  if (redline.currentEntry || !redline.isRemoved) return undefined;
  if (!redline.isResolved) return RemovedRowRedlineTone.Incoming;
  if (redline.isNewlyRemoved) return RemovedRowRedlineTone.Own;
  return RemovedRowRedlineTone.Settled;
}

/** The colour of the line struck through a removed row. Blue is the blue-line tone every redline uses. */
export function removedRowRedlineColour(theme: Theme, tone: RemovedRowRedlineTone): string {
  switch (tone) {
    case RemovedRowRedlineTone.Incoming:
      return theme.palette.error.main;
    case RemovedRowRedlineTone.Own:
      return lightBlue[700];
    case RemovedRowRedlineTone.Settled:
      return theme.palette.text.primary;
  }
}

/**
 * Every cell of a removed row struck through, each over its own text, in one colour (#7084).
 *
 * Each cell's redline used to pick its own strike colour, so one removed row could read black, red and
 * blue at once, and a cell with no redline of its own (the team member's email, a removed member's
 * company) was not struck at all. The strike is declared on every element in each cell, not only on
 * the cell: a line-through does not carry into an inline-block or inline-flex child, which is how the
 * member name and the avatar line are laid out. Declaring the colour on each element also overrides
 * the colour a cell's own redline gives words it does not strike.
 *
 * An element that strikes its own text keeps its own line, and so does everything in it (`own-strike.ts`):
 * a word a revision deleted, or a member who left the team, was struck before this removal or by the
 * other party, and its colour says which. So one removed row can still read in two colours.
 *
 * Nothing that holds such an element is struck either, the cell included. A line-through on a box is
 * drawn through all its in-flow descendants, in the box's colour, so a struck cell would draw the row's
 * line through the element's own as well. The text beside it is struck on its own elements.
 *
 * A single line drawn edge to edge across the row was tried and rejected: the strike stays on the text.
 */
export function removedRowRedlineStyles(tone: RemovedRowRedlineTone): (theme: Theme) => SystemStyleObject<Theme> {
  return (theme) => {
    const strike = {
      textDecorationColor: removedRowRedlineColour(theme, tone),
      textDecorationLine: "line-through",
      textDecorationThickness: "2px",
    };
    const own = `[${OWN_STRIKE_ATTRIBUTE}]`;
    return { [`& > td:not(:has(${own})), & > td *:not(${own}, ${own} *, :has(${own}))`]: strike };
  };
}
